import { apiClient } from '@/lib/api-client';
import type {
  Paginated,
  Subject,
  SubjectView,
  SubjectViewDetail,
  SubjectViewMembership,
  SubjectEdge,
  ContentItem,
  ContentItemListEntry,
  ContentItemDetail,
  ExamFilterOptions,
  QuestionFilterOptions,
  QuestionSearchCard,
  SearchQuestionsInput,
  ContentSubjectLink,
  ContentLink,
  ListSubjectsInput,
  SubjectList,
  ListContentItemsInput,
  CreateSubjectInput,
  RenameSubjectInput,
  CreateSubjectViewInput,
  AddMembershipInput,
  AddSubjectEdgeInput,
  CreateContentItemInput,
  ClassifyContentInput,
  SetContentClassificationInput,
  SetQuestionOfficialCommentInput,
  OfficialCommentWorkspace,
  ExamPreviewOfficialComment,
  LinkContentInput,
  CreateInstitutionInput,
  Institution,
  ListInstitutionsInput,
  UpdateInstitutionInput,
  MergeInstitutionsResult,
  CreateExamQuestionInput,
  QuestionImageInput,
  ExamQuestion,
  ExamPreview,
  ValidateExamImportInput,
  ValidateExamImportResult,
  BulkDeleteQuestionsResult,
  ContentEditLockResult,
  MoveExamQuestionsInput,
  MoveExamQuestionsResult,
  SetQuestionAnswerKeyInput,
} from '../../domain/entities/content.entity';

/**
 * Uma classe, um método por endpoint. Sem lógica de negócio — só HTTP. Leitura
 * em `ContentAdminReadController` (`GET /admin/content/*`); escrita nos
 * controllers de ação (subjects/subject-views/subject-edges/items/...).
 */
export class ContentApiDatasource {
  // ---- Subjects ----------------------------------------------------------

  async listSubjects(input?: ListSubjectsInput): Promise<SubjectList> {
    const { data } = await apiClient.get<SubjectList>(
      '/admin/content/subjects',
      {
        params: {
          page: input?.page ?? 1,
          perPage: input?.perPage ?? 10,
          search: input?.search || undefined,
          includeDeleted: input?.includeDeleted || undefined,
        },
      },
    );
    return data;
  }

  async getSubject(subjectId: string): Promise<Subject> {
    const { data } = await apiClient.get<Subject>(
      `/admin/content/subjects/${subjectId}`,
    );
    return data;
  }

  async createSubject(input: CreateSubjectInput): Promise<Subject> {
    const { data } = await apiClient.post<Subject>(
      '/admin/content/subjects',
      input,
    );
    return data;
  }

  async renameSubject(
    subjectId: string,
    input: RenameSubjectInput,
  ): Promise<Subject> {
    const { data } = await apiClient.patch<Subject>(
      `/admin/content/subjects/${subjectId}`,
      input,
    );
    return data;
  }

  async deleteSubject(subjectId: string): Promise<Subject> {
    const { data } = await apiClient.delete<Subject>(
      `/admin/content/subjects/${subjectId}`,
    );
    return data;
  }

  // ---- Subject views + memberships --------------------------------------

  async listSubjectViews(): Promise<SubjectView[]> {
    const { data } = await apiClient.get<SubjectView[]>(
      '/admin/content/subject-views',
    );
    return data;
  }

  async getSubjectView(viewId: string): Promise<SubjectViewDetail> {
    const { data } = await apiClient.get<SubjectViewDetail>(
      `/admin/content/subject-views/${viewId}`,
    );
    return data;
  }

  async createSubjectView(input: CreateSubjectViewInput): Promise<SubjectView> {
    const { data } = await apiClient.post<SubjectView>(
      '/admin/content/subject-views',
      input,
    );
    return data;
  }

  async addMembership(
    viewId: string,
    input: AddMembershipInput,
  ): Promise<SubjectViewMembership> {
    const { data } = await apiClient.post<SubjectViewMembership>(
      `/admin/content/subject-views/${viewId}/members`,
      input,
    );
    return data;
  }

  async removeMembership(viewId: string, subjectId: string): Promise<void> {
    await apiClient.delete(
      `/admin/content/subject-views/${viewId}/members/${subjectId}`,
    );
  }

  // ---- Subject edges -----------------------------------------------------

  async listSubjectEdges(params?: {
    parentId?: string;
    childId?: string;
  }): Promise<SubjectEdge[]> {
    const { data } = await apiClient.get<SubjectEdge[]>(
      '/admin/content/subject-edges',
      {
        params: {
          parentId: params?.parentId || undefined,
          childId: params?.childId || undefined,
        },
      },
    );
    return data;
  }

  async addSubjectEdge(input: AddSubjectEdgeInput): Promise<SubjectEdge> {
    const { data } = await apiClient.post<SubjectEdge>(
      '/admin/content/subject-edges',
      input,
    );
    return data;
  }

  async removeSubjectEdge(parentId: string, childId: string): Promise<void> {
    await apiClient.delete(
      `/admin/content/subject-edges/${parentId}/${childId}`,
    );
  }

  // ---- Content items -----------------------------------------------------

  async listContentItems(
    input?: ListContentItemsInput,
  ): Promise<Paginated<ContentItemListEntry>> {
    const { data } = await apiClient.get<Paginated<ContentItemListEntry>>(
      '/admin/content/items',
      {
        params: {
          page: input?.page ?? 1,
          perPage: input?.perPage ?? 10,
          type: input?.type || undefined,
          // Lista vira CSV (`status=DRAFT,PUBLISHED`) — a forma que o
          // `ListContentItemsQueryDto` do back destrincha.
          status: Array.isArray(input?.status)
            ? input.status.join(',') || undefined
            : input?.status || undefined,
          includeDeleted: input?.includeDeleted || undefined,
          sortBy: input?.sortBy || undefined,
          sortDirection: input?.sortDirection || undefined,
          questionFormat: input?.questionFormat,
          annulled: input?.annulled,
          examId: input?.examId || undefined,
          // Filtros de prova: `year` vai como número, e `variant` vazio não
          // vai (o back recusa `variant=''` com 400).
          uf: input?.uf || undefined,
          institutionId: input?.institutionId || undefined,
          year: input?.year,
          variant: input?.variant || undefined,
          examKind: input?.examKind,
        },
      },
    );
    return data;
  }

  /** As opções da busca de provas para uma instituição (e, opcional, um ano). */
  async getExamFilterOptions(
    institutionId: string,
    year?: number,
  ): Promise<ExamFilterOptions> {
    const { data } = await apiClient.get<ExamFilterOptions>(
      '/admin/content/exam-filter-options',
      { params: { institutionId, year } },
    );
    return data;
  }

  /**
   * A busca de questões do admin (DESEN-42/43). `year` vai como número, e
   * filtro vazio não vai: o back valida `subjectId`/`institutionId` como UUID,
   * e `''` daria 400.
   */
  async searchQuestions(
    input: SearchQuestionsInput,
  ): Promise<Paginated<QuestionSearchCard>> {
    const { data } = await apiClient.get<Paginated<QuestionSearchCard>>(
      '/admin/content/questions/search',
      {
        params: {
          page: input.page ?? 1,
          perPage: input.perPage ?? 10,
          subjectId: input.subjectId || undefined,
          uf: input.uf || undefined,
          institutionId: input.institutionId || undefined,
          year: input.year,
          demand: input.demand || undefined,
        },
      },
    );
    return data;
  }

  /** Os filhos de um assunto (sem ele, as Grandes Áreas) e os anos das provas. */
  async getQuestionFilterOptions(
    parentSubjectId?: string,
  ): Promise<QuestionFilterOptions> {
    const { data } = await apiClient.get<QuestionFilterOptions>(
      '/admin/content/question-filter-options',
      { params: { parentSubjectId: parentSubjectId || undefined } },
    );
    return data;
  }

  async getContentItem(contentId: string): Promise<ContentItemDetail> {
    const { data } = await apiClient.get<ContentItemDetail>(
      `/admin/content/items/${contentId}`,
    );
    return data;
  }

  async createContentItem(input: CreateContentItemInput): Promise<ContentItem> {
    const { data } = await apiClient.post<ContentItem>(
      '/admin/content/items',
      input,
    );
    return data;
  }

  async updateContentItemBody(
    contentId: string,
    body: unknown,
    images?: QuestionImageInput[],
  ): Promise<ContentItem> {
    const { data } = await apiClient.patch<ContentItem>(
      `/admin/content/items/${contentId}`,
      // `images` só tem efeito quando o item é QUESTION; omitido quando não há
      // imagem nova (as já salvas vivem como token no próprio texto).
      { body, ...(images?.length ? { images } : {}) },
    );
    return data;
  }

  async publishContentItem(contentId: string): Promise<ContentItem> {
    const { data } = await apiClient.patch<ContentItem>(
      `/admin/content/items/${contentId}/publish`,
    );
    return data;
  }

  async archiveContentItem(contentId: string): Promise<ContentItem> {
    const { data } = await apiClient.patch<ContentItem>(
      `/admin/content/items/${contentId}/archive`,
    );
    return data;
  }

  async deleteContentItem(contentId: string): Promise<ContentItem> {
    const { data } = await apiClient.delete<ContentItem>(
      `/admin/content/items/${contentId}`,
    );
    return data;
  }

  /** Delete físico: só `DRAFT`, e a prova leva junto as questões que contém. */
  async hardDeleteContentItem(contentId: string): Promise<void> {
    await apiClient.delete(`/admin/content/items/${contentId}/hard`);
  }

  // ---- Classificações (conteúdo↔subject) --------------------------------

  async classifyContent(
    contentId: string,
    input: ClassifyContentInput,
  ): Promise<ContentSubjectLink> {
    const { data } = await apiClient.post<ContentSubjectLink>(
      `/admin/content/items/${contentId}/subjects`,
      input,
    );
    return data;
  }

  async setSubjectRelation(
    contentId: string,
    subjectId: string,
    relation: string,
  ): Promise<ContentSubjectLink> {
    const { data } = await apiClient.patch<ContentSubjectLink>(
      `/admin/content/items/${contentId}/subjects/${subjectId}`,
      { relation },
    );
    return data;
  }

  /** Reclassificar: repõe a classificação inteira numa requisição/transação. */
  async setContentClassification(
    contentId: string,
    input: SetContentClassificationInput,
  ): Promise<ContentSubjectLink[]> {
    const { data } = await apiClient.put<ContentSubjectLink[]>(
      `/admin/content/items/${contentId}/subjects`,
      input,
    );
    return data;
  }

  async unclassifyContent(contentId: string, subjectId: string): Promise<void> {
    await apiClient.delete(
      `/admin/content/items/${contentId}/subjects/${subjectId}`,
    );
  }

  // ---- Relações (conteúdo↔conteúdo) -------------------------------------

  async linkContent(
    contentId: string,
    input: LinkContentInput,
  ): Promise<ContentLink> {
    const { data } = await apiClient.post<ContentLink>(
      `/admin/content/items/${contentId}/links`,
      input,
    );
    return data;
  }

  async unlinkContent(
    contentId: string,
    targetContentId: string,
    relation: string,
  ): Promise<void> {
    await apiClient.delete(
      `/admin/content/items/${contentId}/links/${targetContentId}/${relation}`,
    );
  }

  // ---- Instituições ------------------------------------------------------

  async listInstitutions(
    input?: ListInstitutionsInput,
  ): Promise<Paginated<Institution>> {
    const { data } = await apiClient.get<Paginated<Institution>>(
      '/admin/content/institutions',
      {
        params: {
          page: input?.page ?? 1,
          perPage: input?.perPage ?? 10,
          search: input?.search || undefined,
          includeDeleted: input?.includeDeleted || undefined,
          // `undefined` some da query; `false` é filtro legítimo ("só as de
          // fora"), então só o ausente é omitido.
          isInHouse: input?.isInHouse,
        },
      },
    );
    return data;
  }

  async createInstitution(input: CreateInstitutionInput): Promise<Institution> {
    const { data } = await apiClient.post<Institution>(
      '/admin/content/institutions',
      input,
    );
    return data;
  }

  async updateInstitution(
    institutionId: string,
    input: UpdateInstitutionInput,
  ): Promise<Institution> {
    const { data } = await apiClient.patch<Institution>(
      `/admin/content/institutions/${institutionId}`,
      input,
    );
    return data;
  }

  /**
   * **Arquivar** — o verbo HTTP é `DELETE` porque a mecânica é o soft delete
   * que já existia; a palavra na tela é "arquivar". Recusa com 409
   * `INSTITUTION_IN_USE` se houver prova apontando.
   */
  async archiveInstitution(institutionId: string): Promise<Institution> {
    const { data } = await apiClient.delete<Institution>(
      `/admin/content/institutions/${institutionId}`,
    );
    return data;
  }

  async restoreInstitution(institutionId: string): Promise<Institution> {
    const { data } = await apiClient.post<Institution>(
      `/admin/content/institutions/${institutionId}/restore`,
    );
    return data;
  }

  /**
   * **Mesclar**: `sourceInstitutionId` some dentro de `targetInstitutionId`, e
   * as provas da origem trocam de instituição.
   *
   * Sem corpo — os dois ids vão no path e o ator sai do token. A rota é
   * `@StrictRoles(systemAdministrator)` no back: `administrator` leva 403 mesmo
   * conseguindo abrir a tela.
   *
   * Recusa com 409 `INSTITUTION_MERGE_CONFLICT` (coordenada duplicada, colisões
   * no `details`) ou 422 `INSTITUTION_MERGE_TOO_LARGE` (acima de 500 provas).
   */
  async mergeInstitutions(
    sourceInstitutionId: string,
    targetInstitutionId: string,
  ): Promise<MergeInstitutionsResult> {
    const { data } = await apiClient.post<MergeInstitutionsResult>(
      `/admin/content/institutions/${sourceInstitutionId}/merge-into/${targetInstitutionId}`,
    );
    return data;
  }

  // ---- Provas: importação, preview e questões -----------------------------

  async validateExamImport(
    input: ValidateExamImportInput,
  ): Promise<ValidateExamImportResult> {
    const { data } = await apiClient.post<ValidateExamImportResult>(
      '/admin/content/exams/validate-import',
      input,
    );
    return data;
  }

  async getExamPreview(examId: string): Promise<ExamPreview> {
    const { data } = await apiClient.get<ExamPreview>(
      `/admin/content/exams/${examId}/preview`,
    );
    return data;
  }

  async createExamQuestion(
    examId: string,
    input: CreateExamQuestionInput,
  ): Promise<ExamQuestion> {
    const { data } = await apiClient.post<ExamQuestion>(
      `/admin/content/exams/${examId}/questions`,
      input,
    );
    return data;
  }

  /** Comentário oficial da questão: cria, atualiza ou (texto vazio) remove. */
  async setQuestionOfficialComment(
    questionId: string,
    input: SetQuestionOfficialCommentInput,
  ): Promise<ExamPreviewOfficialComment | null> {
    const { data } = await apiClient.put<ExamPreviewOfficialComment | null>(
      `/admin/content/questions/${questionId}/official-comment`,
      input,
    );
    return data;
  }

  /** A questão e o comentário juntos, para a página do comentário oficial. */
  async getOfficialCommentWorkspace(
    questionId: string,
  ): Promise<OfficialCommentWorkspace> {
    const { data } = await apiClient.get<OfficialCommentWorkspace>(
      `/admin/content/questions/${questionId}/official-comment`,
    );
    return data;
  }

  /** Tira do ar sem apagar: `PUBLISHED → DRAFT`. Republicar é o publish genérico. */
  async unpublishOfficialComment(questionId: string): Promise<void> {
    await apiClient.patch(
      `/admin/content/questions/${questionId}/official-comment/unpublish`,
    );
  }

  async reorderExamQuestion(
    examId: string,
    questionId: string,
    newPosition: number,
  ): Promise<ContentLink[]> {
    const { data } = await apiClient.patch<ContentLink[]>(
      `/admin/content/exams/${examId}/questions/${questionId}/position`,
      { newPosition },
    );
    return data;
  }

  /** Tira a questão do simulado (desfaz o apontador); a questão segue viva. */
  async removeExamQuestionReference(
    examId: string,
    questionId: string,
  ): Promise<void> {
    await apiClient.delete(
      `/admin/content/exams/${examId}/questions/${questionId}/reference`,
    );
  }

  async bulkDeleteQuestions(
    questionIds: string[],
  ): Promise<BulkDeleteQuestionsResult[]> {
    // DELETE com body: o axios exige `data` dentro do config (não é o 2º arg).
    const { data } = await apiClient.delete<BulkDeleteQuestionsResult[]>(
      '/admin/content/questions/bulk',
      { data: { questionIds } },
    );
    return data;
  }

  /**
   * Gabarito de uma questão. Rota própria, e não o `PATCH` de body inteiro: o
   * body que a tela do gabarito tem em mão é o do preview, com `<img>` no lugar
   * do token — e `img` não passa no allowlist de escrita, então reenviá-lo
   * apagaria as imagens da questão.
   */
  async setQuestionAnswerKey(
    questionId: string,
    input: SetQuestionAnswerKeyInput,
  ): Promise<ContentItem> {
    const { data } = await apiClient.patch<ContentItem>(
      `/admin/content/questions/${questionId}/answer-key`,
      input,
    );
    return data;
  }

  async moveExamQuestions(
    input: MoveExamQuestionsInput,
  ): Promise<MoveExamQuestionsResult[]> {
    const { data } = await apiClient.post<MoveExamQuestionsResult[]>(
      '/admin/content/questions/bulk-move',
      input,
    );
    return data;
  }

  // ---- Lock de edição ----------------------------------------------------

  /** Aquisição e heartbeat: o mesmo `POST`, com todos os ids em edição. */
  async acquireEditLocks(
    contentIds: string[],
  ): Promise<ContentEditLockResult[]> {
    const { data } = await apiClient.post<ContentEditLockResult[]>(
      '/admin/content/edit-locks',
      { contentIds },
    );
    return data;
  }

  async releaseEditLocks(contentIds: string[]): Promise<void> {
    // DELETE com body: o axios exige `data` dentro do config (não é o 2º arg).
    await apiClient.delete('/admin/content/edit-locks', {
      data: { contentIds },
    });
  }
}
